import React from "react";

import { Card, CardContent, CardHeader, CircularProgress, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { IDashboardData } from "../../../models/overview/dashboard";
import { SupplyChainOverviewConfigure } from "../../../models/supply.chain.overview.configure";
import NotEnoughData from "../../DashMetrics/NotEnoughData";
import { useSupplyChainOverviewContext } from "../../DashMetrics/SupplyChainOverview/context";
import OverviewBarGraph from "./Bar";
import OverviewBubbleMapGraph from "./BubbleMap";
import OverviewCardGraph from "./Card";
import OverviewDoughnutGraph from "./Doughnut";
import OverviewLineGraph from "./Line";
import OverviewMapGraph from "./Map";
import OverviewRatioGraph from "./Ratio";
import useStyles from "./styles";
import OverviewTable from "./Table";

const DRAG_ICON = require("Assets/icons/drag-button.svg");

interface Props {
  panel: keyof SupplyChainOverviewConfigure;
  widget: string;
}

const OverviewGraph = ({ panel, widget }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const { customDate, customPeriod, dashboard, handleWidgetConfigChange, selectedPeriod } =
    useSupplyChainOverviewContext();
  const dashboardProps = dashboard[panel]?.graphs[widget];

  if (!dashboardProps?.useQuery) return null;

  const { title, type, useQuery } = dashboardProps;
  const { data, isFetching } = useQuery({ customDate, customPeriod, selectedPeriod });

  const renderAction = () => {
    return (
      <IconButton onClick={() => handleWidgetConfigChange(panel, widget, false)}>
        <Close />
      </IconButton>
    );
  };

  const renderGraph = () => {
    if (isFetching) {
      return (
        <div className={classes.fetchLoading}>
          <CircularProgress />
        </div>
      );
    }

    if (!data) return <NotEnoughData />;

    const { colors, datasets, labels, rowActions, total } = data;

    switch (type) {
      case "bar":
        return (
          <OverviewBarGraph
            colors={colors}
            datasets={datasets as IDashboardData[]}
            labels={labels as string[]}
          />
        );
      case "bubblemap":
        return <OverviewBubbleMapGraph datasets={datasets as IDashboardData[]} />;
      case "card":
        return (
          <OverviewCardGraph
            data={(datasets as IDashboardData[]).flatMap((dataset) => dataset.data as number[])}
            labels={labels as string[]}
            total={total || 0}
          />
        );
      case "doughnut":
        return (
          <OverviewDoughnutGraph
            colors={colors}
            datasets={datasets as IDashboardData[]}
            labels={labels as string[]}
          />
        );
      case "line":
        return (
          <OverviewLineGraph datasets={datasets as IDashboardData[]} labels={labels as string[]} />
        );
      case "map":
        return <OverviewMapGraph datasets={datasets as IDashboardData[]} />;
      case "ratio":
        return (
          <OverviewRatioGraph
            data={(datasets as IDashboardData[]).flatMap((dataset) => dataset.data as number[])}
            labels={labels as string[]}
            total={total || 0}
          />
        );
      case "table":
        return (
          <OverviewTable columns={labels as Column[]} data={datasets} rowActions={rowActions} />
        );
      default:
        return <>{title} - DEFAULT</>;
    }
  };

  const renderTitle = () => {
    return (
      <>
        <IconButton className="drag-handle" size="small">
          <img src={DRAG_ICON} />
        </IconButton>
        <span style={{ paddingLeft: "8px" }}>{title && translate(title)}</span>
      </>
    );
  };

  return (
    <Card className={classes.card}>
      {title && (
        <CardHeader action={renderAction()} className={classes.cardHeader} title={renderTitle()} />
      )}
      <CardContent className={classes.cardContent}>
        <div className={classes.responsiveGraphContainer}>{renderGraph()}</div>
      </CardContent>
    </Card>
  );
};

export default OverviewGraph;
